import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { newLineToBr } from '@prophecy/utils/react/nodes';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Type as DialogType } from '../Dialog/types';
import { Ellipsis, MultilineEllipsis } from '../Ellipsis';
import { CheckIcon, CopyFIcon } from '../Icons';
import { Stack, StackItem } from '../Layout';
import { theme } from '../theme';
import { Text } from '../Typography/Text';
import { StyledButton, ToastCopyContainer, StyledSuccessIcon, DialogCopyContainer } from './styled';
import { toast } from './Toast';
import { Variant as ToastTypes } from './types';
export var MessageType = /*#__PURE__*/ function(MessageType) {
    MessageType["info"] = "info";
    MessageType["error"] = "error";
    MessageType["success"] = "success";
    MessageType["warning"] = "warning";
    return MessageType;
}({});
const StyledText = styled(Text)`
  word-break: break-word;
  position: relative;
`;
const StyledContent = styled(Stack)`
  max-height: calc(70vh - 170px); // 170px is the height of fixed items + gaps in between + padding.
  overflow: auto;
`;
const LinkButton = styled(Button)`
  text-decoration: underline;
  text-underline-offset: ${theme.spaces.x4};
  width: max-content;
  padding: 0;
`;
const DetailContainer = styled(Stack)`
  width: 100%;
  height: 100%;
  position: relative;
`;
const CopyButtonContainer = styled(Stack)`
  position: absolute;
  right: ${theme.spaces.x8};
  top: ${theme.spaces.x4};
  z-index: 1;
`;
const MessageTypeDisplayMap = {
    ["info"]: DialogType.info,
    ["error"]: DialogType.alert,
    ["success"]: DialogType.success,
    ["warning"]: DialogType.warning
};
const MessageTypeToastMap = {
    ["info"]: ToastTypes.info,
    ["error"]: ToastTypes.error,
    ["success"]: ToastTypes.success,
    ["warning"]: ToastTypes.error
};
const DEFAULT_DURATION = 10000;
const activeToastMap = {
    ["info"]: new Map(),
    ["error"]: new Map(),
    ["success"]: new Map(),
    ["warning"]: new Map()
};
function CopyToClipBoard({ content, variant }) {
    const [showCopied, setShowCopied] = useState(false);
    const onCopyToClipboard = (content)=>{
        let messageContent = '';
        if (typeof content === 'string') {
            messageContent += content;
        } else if (/*#__PURE__*/ React.isValidElement(content)) {
            const iterateOverChildren = (children)=>{
                if (!children) {
                    return;
                }
                React.Children.forEach(children, (child)=>{
                    if (typeof child === 'string') {
                        messageContent += child;
                    } else if (/*#__PURE__*/ React.isValidElement(child)) {
                        iterateOverChildren(child.props.children);
                    }
                });
            };
            iterateOverChildren(content?.props?.children);
        }
        if (messageContent) {
            navigator.clipboard.writeText(messageContent);
            setShowCopied(true);
            setTimeout(()=>{
                setShowCopied(false);
            }, 1000);
        }
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: showCopied ? /*#__PURE__*/ _jsx(StyledSuccessIcon, {
            onClick: ()=>onCopyToClipboard?.(content),
            icon: /*#__PURE__*/ _jsx(CheckIcon, {
                type: "default",
                size: "xs"
            }),
            variant: "link",
            "aria-label": "Copied to Clipboard",
            "data-test-id": "toast-copy-button"
        }) : /*#__PURE__*/ _jsx(StyledButton, {
            tooltip: "Copy to Clipboard",
            onClick: ()=>onCopyToClipboard?.(content),
            icon: /*#__PURE__*/ _jsx(CopyFIcon, {
                type: "default",
                size: "xs"
            }),
            variant: variant,
            "aria-label": "Copy to Clipboard",
            "data-test-id": "toast-copy-button"
        })
    });
}
export function MessageDetail({ content, detail }) {
    if (!content || typeof content === 'string' && !content.trim()) return null;
    const _content = newLineToBr(content);
    return /*#__PURE__*/ _jsxs(StyledContent, {
        gap: "1rem",
        children: [
            /*#__PURE__*/ _jsx(StyledText, {
                level: "sm",
                children: _content
            }),
            ' ',
            detail ? /*#__PURE__*/ _jsxs(StyledText, {
                level: "sm",
                children: [
                    /*#__PURE__*/ _jsx(DialogCopyContainer, {
                        children: /*#__PURE__*/ _jsx(CopyToClipBoard, {
                            content: detail,
                            variant: "link"
                        })
                    }),
                    newLineToBr(detail)
                ]
            }) : null
        ]
    });
}
function ShowMore({ children, defaultShow = false }) {
    const [show, setShow] = useState(defaultShow);
    return show ? /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    }) : /*#__PURE__*/ _jsx(LinkButton, {
        size: "s",
        variant: "link",
        onClick: ()=>setShow(true),
        "data-test-id": "toast-more-details-button",
        children: "More Details"
    });
}
function base(messageType, config) {
    const { content, title = /*#__PURE__*/ _jsx(Text, {
        level: "sm",
        children: "Details"
    }), detail, detailText, onDetailButtonClick, duration = DEFAULT_DURATION, detailButtonText = 'More..', copyToClipBoard = false, width: _width, collapseDetail = false, 'data-test-id': dataTestId } = config;
    if (!content || typeof content === 'string' && !content.trim()) return;
    // if content is too long or detail is present, add More button
    const hasDetailsToShow = detail || typeof content === 'string' && content.length > 120;
    const showMoreButton = hasDetailsToShow || Boolean(onDetailButtonClick);
    const _content = newLineToBr(content);
    const width = _width || (hasDetailsToShow ? 500 : 400);
    //width - More button width +padding + border
    const contentWidth = showMoreButton ? width - 210 : width - 90;
    const showDetailModal = ()=>{
        const dialogType = MessageTypeDisplayMap[messageType];
        const copyButton = /*#__PURE__*/ _jsx(DialogCopyContainer, {
            children: /*#__PURE__*/ _jsx(CopyToClipBoard, {
                content: detailText || detail || content,
                variant: "link"
            })
        });
        let detailToRender = null;
        if (detail && typeof detail === 'string') {
            detailToRender = /*#__PURE__*/ _jsxs(StyledText, {
                level: "sm",
                children: [
                    copyButton,
                    newLineToBr(detail)
                ]
            });
        } else if (detail) {
            detailToRender = /*#__PURE__*/ _jsxs(DetailContainer, {
                children: [
                    detail,
                    /*#__PURE__*/ _jsx(CopyButtonContainer, {
                        children: copyButton
                    })
                ]
            });
        }
        Dialog[dialogType]({
            children: /*#__PURE__*/ _jsxs(StyledContent, {
                gap: theme.spaces.x8,
                children: [
                    /*#__PURE__*/ _jsxs(StyledText, {
                        level: "sm",
                        children: [
                            !detail && copyButton,
                            _content
                        ]
                    }),
                    ' ',
                    detailToRender && /*#__PURE__*/ _jsx(ShowMore, {
                        defaultShow: !collapseDetail,
                        children: detailToRender
                    })
                ]
            }),
            title,
            size: 'l',
            iconPlacement: 'right',
            closeButton: null,
            'data-test-id': `toast-detail-${dialogType}-dialog`
        });
    };
    const buttonVariantProps = messageType === "error" ? {
        variant: 'secondary',
        danger: true
    } : {
        variant: 'secondaryGray',
        danger: false
    };
    const messageContent = /*#__PURE__*/ _jsxs(Stack, {
        direction: "horizontal",
        gap: theme.spaces.x12,
        alignY: "center",
        align: "space-between",
        width: "100%",
        children: [
            /*#__PURE__*/ _jsx(Stack, {
                width: contentWidth + 'px',
                "data-test-id": `${dataTestId ? `${dataTestId}-` : 'toast-'}content-wrapper`,
                children: showMoreButton ? /*#__PURE__*/ _jsx(Ellipsis, {
                    tooltip: true,
                    tooltipProps: {
                        overlayStyle: {
                            width,
                            wordBreak: 'break-all'
                        }
                    },
                    children: _content
                }) : /*#__PURE__*/ _jsx("p", {
                    children: /*#__PURE__*/ _jsx(MultilineEllipsis, {
                        lines: 2,
                        expandable: true,
                        symbol: "... more",
                        collapsible: true,
                        children: _content
                    })
                })
            }),
            showMoreButton && /*#__PURE__*/ _jsx(StackItem, {
                shrink: "0",
                children: /*#__PURE__*/ _jsx(Button, {
                    ...buttonVariantProps,
                    size: "s",
                    onClick: ()=>{
                        // if we do custom handing of detail button click, we don't want to show the detail modal
                        if (hasDetailsToShow && !onDetailButtonClick) showDetailModal();
                        onDetailButtonClick?.();
                    },
                    "data-test-id": "toast-more-details-button",
                    children: detailButtonText
                })
            }),
            copyToClipBoard && /*#__PURE__*/ _jsx(StackItem, {
                shrink: "0",
                children: /*#__PURE__*/ _jsx(ToastCopyContainer, {
                    children: /*#__PURE__*/ _jsx(CopyToClipBoard, {
                        content: content,
                        variant: "linkGray"
                    })
                })
            })
        ]
    });
    const toastType = MessageTypeToastMap[messageType];
    /**
   * find existing toast with same content and close it
   */ if (activeToastMap[messageType].has(content)) {
        const activeToast = activeToastMap[messageType].get(content);
        if (activeToast) {
            activeToast.close();
        }
    }
    const currentToast = toast[toastType]({
        content: messageContent,
        duration,
        width: `${width}`,
        onClose: ()=>{
            activeToastMap[messageType].delete(content);
        },
        'data-test-id': dataTestId
    });
    activeToastMap[messageType].set(content, currentToast);
    return currentToast;
}
function error(config) {
    return base("error", {
        copyToClipBoard: true,
        ...config
    });
}
function info(config) {
    return base("info", config);
}
function success(config) {
    return base("success", config);
}
function warning(config) {
    return base("warning", config);
}
export const message = {
    error,
    info,
    success,
    warning
};
